import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Fab,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  LocalOffer as CouponIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  History as HistoryIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingIcon
} from '@mui/icons-material';
import adminAuthService from '../services/adminAuthService';
import { API_BASE_URL } from '../config/api';

const CouponManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [coupons, setCoupons] = useState([]);
  const [couponStats, setCouponStats] = useState({
    total_coupons: 0,
    active_coupons: 0,
    used_coupons: 0,
    total_discount: 0
  });
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [couponUsages, setCouponUsages] = useState([]);

  // Form states
  const [formData, setFormData] = useState({
    code: '',
    type: 'percent',
    value: '',
    is_active: true,
    end_date: '',
    show_in_giftshop: true,
    points_price: 1,
    usage_limit: ''
  });

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!adminAuthService.isAuthenticated() || !adminAuthService.isAdmin()) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_coupons.php`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('فشل في تحميل الكوبونات');
      }

      const data = await response.json();
      if (data.success) {
        setCoupons(data.coupons || []);
        setCouponStats(data.stats || {});
      } else {
        throw new Error(data.message || 'فشل في تحميل البيانات');
      }
    } catch (error) {
      console.error('Error loading coupons:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async () => {
    try {
      setError('');
      const token = adminAuthService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/api/admin_coupons.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'create',
          ...formData
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('تم إنشاء الكوبون بنجاح');
        setDialogOpen(false);
        resetForm();
        loadData();
      } else {
        throw new Error(data.message || 'فشل في إنشاء الكوبون');
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleUpdateCoupon = async () => {
    try {
      setError('');
      const token = adminAuthService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/api/admin_coupons.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'update',
          id: selectedCoupon.id,
          ...formData
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('تم تحديث الكوبون بنجاح');
        setDialogOpen(false);
        resetForm();
        loadData();
      } else {
        throw new Error(data.message || 'فشل في تحديث الكوبون');
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الكوبون؟')) return;
    
    try {
      setError('');
      const token = adminAuthService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/api/admin_coupons.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'delete',
          id: couponId
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('تم حذف الكوبون بنجاح');
        loadData();
      } else {
        throw new Error(data.message || 'فشل في حذف الكوبون');
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleViewUsages = async (coupon) => {
    try {
      setSelectedCoupon(coupon);
      setError('');
      const token = adminAuthService.getToken();
      
      const response = await fetch(`${API_BASE_URL}/api/admin_coupons.php?action=usages&coupon_id=${coupon.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setCouponUsages(data.usages || []);
        setUsageDialogOpen(true);
      } else {
        throw new Error(data.message || 'فشل في تحميل بيانات الاستخدام');
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setSelectedCoupon(null);
    setDialogOpen(true);
  };

  const openEditDialog = (coupon) => {
    setSelectedCoupon(coupon);
    
    // Map backend property names to form data
    // Backend returns: discount_type, discount_value, active, expires_at (original column names)
    // Backend also has aliases: type, value, is_active, end_date
    const couponType = coupon.type || coupon.discount_type || 'percent';
    const couponValue = coupon.value || coupon.discount_value || '';
    const isActive = coupon.is_active !== undefined ? coupon.is_active : coupon.active;
    const endDate = coupon.end_date || coupon.expires_at || '';
    
    setFormData({
      code: coupon.code || '',
      type: couponType === 'percentage' ? 'percent' : couponType,
      value: couponValue,
      is_active: isActive === 1 || isActive === '1' || isActive === true,
      end_date: endDate ? endDate.split(' ')[0] : '',
      show_in_giftshop: coupon.show_in_giftshop === 1 || coupon.show_in_giftshop === '1',
      points_price: coupon.points_price || 1,
      usage_limit: coupon.usage_limit || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      type: 'percent',
      value: '',
      is_active: true,
      end_date: '',
      show_in_giftshop: true,
      points_price: 1,
      usage_limit: ''
    });
  };

  const generateCouponCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({...formData, code: result});
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-IQ', {
      style: 'currency',
      currency: 'IQD'
    }).format(amount || 0);
  };

  const getStatusColor = (coupon) => {
    if (!coupon.is_active) return 'error';
    
    const now = new Date();
    const endDate = new Date(coupon.end_date);
    const startDate = new Date(coupon.start_date);
    
    if (now < startDate) return 'warning';
    if (now > endDate) return 'error';
    
    const usedCount = parseInt(coupon.used_count || 0);
    const usageLimit = parseInt(coupon.usage_limit || 0);
    
    if (usageLimit > 0 && usedCount >= usageLimit) return 'error';
    
    return 'success';
  };

  const getStatusText = (coupon) => {
    if (!coupon.is_active) return 'غير نشط';
    
    const now = new Date();
    const endDate = new Date(coupon.end_date);
    const startDate = new Date(coupon.start_date);
    
    if (now < startDate) return 'لم يبدأ بعد';
    if (now > endDate) return 'منتهي الصلاحية';
    
    const usedCount = parseInt(coupon.used_count || 0);
    const usageLimit = parseInt(coupon.usage_limit || 0);
    
    if (usageLimit > 0 && usedCount >= usageLimit) return 'مستنفد';
    
    return 'نشط';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <AppBar position="static">
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')}>
            <BackIcon />
          </IconButton>
          <CouponIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            إدارة الكوبونات
          </Typography>
          <Button
            color="inherit"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            إضافة كوبون
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Alert Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CouponIcon sx={{ color: '#2196f3', mr: 1 }} />
                  <Typography variant="h6">إجمالي الكوبونات</Typography>
                </Box>
                <Typography variant="h4" sx={{ color: '#2196f3', fontWeight: 'bold' }}>
                  {couponStats.total_coupons || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon sx={{ color: '#4caf50', mr: 1 }} />
                  <Typography variant="h6">الكوبونات النشطة</Typography>
                </Box>
                <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                  {couponStats.active_coupons || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ShoppingIcon sx={{ color: '#ff9800', mr: 1 }} />
                  <Typography variant="h6">الكوبونات المستخدمة</Typography>
                </Box>
                <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                  {couponStats.used_coupons || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PeopleIcon sx={{ color: '#9c27b0', mr: 1 }} />
                  <Typography variant="h6">إجمالي الخصومات</Typography>
                </Box>
                <Typography variant="h4" sx={{ color: '#9c27b0', fontWeight: 'bold' }}>
                  {formatCurrency(couponStats.total_discount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Coupons Table */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            قائمة الكوبونات
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>كود الكوبون</TableCell>
                  <TableCell>النوع</TableCell>
                  <TableCell>القيمة</TableCell>
                  <TableCell>عدد الاستخدامات</TableCell>
                  <TableCell>تاريخ الانتهاء</TableCell>
                  <TableCell>متجر الهدايا</TableCell>
                  <TableCell>سعر النقاط</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coupons.map((coupon) => {
                  // Extract values with fallbacks for different property names
                  const couponType = coupon.type || coupon.discount_type || 'percent';
                  const discountValue = coupon.value || coupon.discount_value || 0;
                  
                  return (
                  <TableRow key={coupon.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                        {coupon.code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={couponType === 'percent' || couponType === 'percentage' ? 'نسبة مئوية' : 'مبلغ ثابت'} 
                        color={couponType === 'percent' || couponType === 'percentage' ? 'primary' : 'secondary'} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {couponType === 'percent' || couponType === 'percentage'
                        ? `${discountValue}%` 
                        : formatCurrency(discountValue)
                      }
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {coupon.used_count || 0} / {coupon.usage_limit || '∞'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {coupon.expires_at ? new Date(coupon.expires_at).toLocaleDateString('ar-EG') : 'غير محدد'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={coupon.show_in_giftshop === 1 || coupon.show_in_giftshop === '1' ? 'نعم' : 'لا'}
                        color={coupon.show_in_giftshop === 1 || coupon.show_in_giftshop === '1' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {coupon.points_price || 1} نقطة
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusText(coupon)}
                        color={getStatusColor(coupon)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="عرض الاستخدامات">
                          <IconButton 
                            size="small" 
                            color="info"
                            onClick={() => handleViewUsages(coupon)}
                          >
                            <HistoryIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="تعديل">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => openEditDialog(coupon)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="حذف">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteCoupon(coupon.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Floating Action Button for Mobile */}
        {isMobile && (
          <Fab
            color="primary"
            aria-label="add"
            sx={{ position: 'fixed', bottom: 16, right: 16 }}
            onClick={openCreateDialog}
          >
            <AddIcon />
          </Fab>
        )}
      </Container>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {selectedCoupon ? 'تعديل الكوبون' : 'إضافة كوبون جديد'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="كود الكوبون"
                    fullWidth
                    required
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder="مثال: SAVE20"
                  />
                  <Button
                    variant="outlined"
                    onClick={generateCouponCode}
                    sx={{ minWidth: 'auto', px: 2 }}
                    title="توليد كود تلقائي"
                  >
                    توليد
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>نوع الخصم</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    label="نوع الخصم"
                  >
                    <MenuItem value="percent">نسبة مئوية</MenuItem>
                    <MenuItem value="fixed">مبلغ ثابت</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label={formData.type === 'percent' ? 'النسبة المئوية (%)' : 'المبلغ (د.ع)'}
                  type="number"
                  fullWidth
                  required
                  value={formData.value}
                  onChange={(e) => setFormData({...formData, value: e.target.value})}
                  inputProps={{ min: 0, max: formData.type === 'percent' ? 100 : undefined }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="تاريخ الانتهاء"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="عدد مرات الاستخدام"
                  type="number"
                  fullWidth
                  value={formData.usage_limit}
                  onChange={(e) => setFormData({...formData, usage_limit: e.target.value})}
                  inputProps={{ min: 1 }}
                  placeholder="اتركه فارغ لاستخدام غير محدود"
                  helperText="عدد مرات استخدام هذا الكوبون بشكل عام"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="سعر النقاط في متجر الهدايا"
                  type="number"
                  fullWidth
                  value={formData.points_price}
                  onChange={(e) => setFormData({...formData, points_price: e.target.value})}
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.show_in_giftshop}
                      onChange={(e) => setFormData({...formData, show_in_giftshop: e.target.checked})}
                    />
                  }
                  label="عرض في متجر الهدايا"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    />
                  }
                  label="نشط"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>إلغاء</Button>
          <Button 
            variant="contained" 
            onClick={selectedCoupon ? handleUpdateCoupon : handleCreateCoupon}
          >
            {selectedCoupon ? 'تحديث' : 'إضافة'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Usage History Dialog */}
      <Dialog
        open={usageDialogOpen}
        onClose={() => setUsageDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          سجل استخدام الكوبون: {selectedCoupon?.code}
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>التاريخ</TableCell>
                  <TableCell>العميل</TableCell>
                  <TableCell>رقم الطلب</TableCell>
                  <TableCell>قيمة الخصم</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {couponUsages.map((usage, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {new Date(usage.used_at || usage.purchase_date).toLocaleDateString('ar-EG')}
                    </TableCell>
                    <TableCell>{usage.customer_name || usage.user_name || 'غير محدد'}</TableCell>
                    <TableCell>#{usage.order_id || 'غير محدد'}</TableCell>
                    <TableCell>{formatCurrency(usage.discount_amount || 0)}</TableCell>
                  </TableRow>
                ))}
                {couponUsages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      لم يتم استخدام هذا الكوبون بعد
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsageDialogOpen(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CouponManagement;